import React from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import format from 'date-fns/format';
import lowerCase from 'lodash/lowerCase';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { Tooltip } from 'antd';
import { onCondoPage, renderNumber, getCondoTextRating } from 'src/utils/index';
import { InfoSvg, RedWarningSvg, MapPinSvg, StarSvg } from 'src/assets/index';
import { CondoMatchTypeEnum, CondoRequestTypeEnum, ICondo } from 'src/common-types/index';
import { MINIMUM_RATING_TO_SHOW } from 'src/constants/index';
import { CondoCarouselHotelImages } from '../condo-carousel-hotel-images';
import { RootState } from 'src/store';
import { IMenuState } from 'src/store/slices/index';
import './style.scss';

interface IMapStateToProps {
  menuStore: IMenuState;
}

interface IProps extends IMapStateToProps, RouteComponentProps, WrappedComponentProps {
  condo: ICondo;
  disabled?: boolean;
  lastRequestType: CondoRequestTypeEnum;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSelect?: () => void;
  matches?: boolean;
}

const ZERO = 0;
const ONE = 1;
const PERCENTS = 100;
const DATE_FORMAT = 'd MMM';

class CondoHotelCardWrapper extends React.Component<IProps, null> {
  getMinPublicPrice = (): number => {
    const { publicPrice } = this.props.condo;

    if (publicPrice) {
      return publicPrice;
    }

    return ZERO;
  };

  onCondoPage = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const { condoId } = this.props.condo;

    onCondoPage(condoId, this.props.history);
  };

  getCondoInfo = (): string[] => {
    const { intl, condo } = this.props;
    const { sleepsCount, apartmentType, bathroomsCount, privacyOccupancy } = condo;
    const condoInfo: string[] = [];

    if (sleepsCount && privacyOccupancy) {
      condoInfo.push(
        `${intl.formatMessage({ id: 'max.sleeps' })} ${sleepsCount} (${lowerCase(
          intl.formatMessage({
            id: 'private',
          }),
        )}: ${privacyOccupancy})`,
      );
    }

    if (apartmentType) {
      condoInfo.push(apartmentType);
    }

    if (bathroomsCount) {
      condoInfo.push(intl.formatMessage({ id: 'bathrooms.count' }, { count: bathroomsCount }));
    }

    return condoInfo;
  };

  render(): React.ReactNode {
    const {
      disabled = false,
      condo,
      lastRequestType,
      onMouseEnter = () => void ZERO,
      onMouseLeave = () => void ZERO,
      onSelect = () => void ZERO,
      matches = true,
      menuStore,
    } = this.props;
    const { items } = menuStore;
    const promo = items?.promo;
    const userCredits = items?.leisureCredits;
    const isSavingsExisted = (promo && Number(userCredits) > ZERO) || !promo;
    const {
      images,
      displayName,
      pricePerNight,
      fullAddress,
      publicPrice,
      savings,
      price,
      matchType,
      availableDates = [],
      totalNights = ZERO,
      rating,
      supplierIdentifier,
      isAllInclusive,
    } = condo;
    const pubPrice = this.getMinPublicPrice();
    const save = (price * PERCENTS) / pubPrice;
    const savePercents = Math.floor(PERCENTS - save);
    const isExact = matchType === CondoMatchTypeEnum.Exact;
    const isExactDisabled = isExact && (!availableDates || availableDates.length === ZERO);
    const isFlex = lastRequestType === CondoRequestTypeEnum.Flexible;
    const isStrict = lastRequestType === CondoRequestTypeEnum.Strict;
    const isAnytime = lastRequestType === CondoRequestTypeEnum.Anytime;
    const hideRating = rating && rating.value === ZERO;

    return (
      <div
        className={`condo-hotel-card ${isAnytime ? 'is-any-time' : ''}`}
        onMouseEnter={() => onMouseEnter()}
        onMouseLeave={() => onMouseLeave()}
        onClick={() => {
          if (matches) {
            onSelect();
          }
        }}
      >
        {disabled && <div className="condo-hotel-card__disabled-overlay" />}
        <div className="condo-hotel-card__wrapper">
          <div className="condo-hotel-card__slider">
            {isAllInclusive ? (
              <div className="condo-hotel-card__label-all-inclusive">
                <p className="condo-hotel-card__label-title">
                  <FormattedMessage id="all.inclusive" />
                </p>
              </div>
            ) : null}
            {savePercents && savePercents > ZERO && !disabled ? (
              <div className={`condo-hotel-card__label ${isAllInclusive ? 'two-label' : ''}`}>
                <p className="condo-hotel-card__label-title">
                  <FormattedMessage id="save" /> {savePercents}%
                </p>
              </div>
            ) : null}
            {(isFlex || isStrict) && availableDates.length ? (
              <div className="condo-hotel-card__date-label">
                <div className="condo-hotel-card__date">
                  <p className="condo-hotel-card__date-text">
                    {format(new Date(availableDates[ZERO].from), DATE_FORMAT)} -{' '}
                    {format(new Date(availableDates[ZERO].to), DATE_FORMAT)}
                  </p>
                </div>
                {availableDates.length > ONE && (
                  <div className="condo-hotel-card__date-more">
                    <p className="condo-hotel-card__date-more-text">
                      +{availableDates.length - ONE} <FormattedMessage id="more" />
                    </p>
                  </div>
                )}
              </div>
            ) : null}
            <CondoCarouselHotelImages images={images} />
          </div>
          <div className="condo-hotel-card__content">
            <div className="condo-hotel-card__text-info-wrapper">
              <div className="condo-hotel-card__info-items">
                <h4 className="condo-hotel-card__info-title" onClick={this.onCondoPage}>
                  {displayName}
                </h4>
                {pricePerNight && (
                  <div className="condo-hotel-card__label-info">
                    <p className="condo-hotel-card__label-text">
                      <FormattedMessage id="from" />
                    </p>
                    <p className="condo-hotel-card__label-price">${renderNumber(pricePerNight)}</p>
                    <p className="condo-hotel-card__label-text">
                      <FormattedMessage id="per.night" />
                    </p>
                  </div>
                )}
              </div>
              <div className="condo-hotel-card__info-description">
                <div className="condo-hotel-card__address-wrapper">
                  <p className="condo-hotel-card__address">{fullAddress}</p>
                  <div className="condo-hotel-card__view-on-map" onClick={() => onSelect()}>
                    <MapPinSvg />
                    <FormattedMessage id="view.on.map" />
                  </div>
                </div>

                <div className="condo-hotel-card__info-apartment">
                  {this.getCondoInfo().map((item, index) => (
                    <div key={index} style={{ whiteSpace: 'nowrap' }}>
                      {item}
                      {index < this.getCondoInfo().length - ONE ? <div className="dot" /> : null}
                    </div>
                  ))}
                </div>
                {pricePerNight && price ? (
                  <div className="condo-hotel-card__map-mobile-info">
                    <div className="condo-hotel-card__label-info-map">
                      <p className="condo-hotel-card__label-text">
                        <FormattedMessage id="from" />
                      </p>
                      <p className="condo-hotel-card__label-price">
                        ${renderNumber(pricePerNight)}/
                        <FormattedMessage id="night" />
                      </p>
                    </div>
                    <div className="condo-hotel-card__price-total">
                      <p className="condo-hotel-card__price-total-text">
                        ${renderNumber(Math.floor(price))}
                      </p>
                      <div className="condo-hotel-card__for-info">
                        <p className="condo-hotel-card__for-info-text">
                          <FormattedMessage id="for" />{' '}
                          <FormattedMessage id="nights" values={{ count: totalNights }} />
                        </p>
                      </div>
                    </div>
                  </div>
                ) : null}
                <div className="condo-hotel-card__rating">
                  <div className="condo-hotel-card__trust-you-info">
                    {rating ? (
                      <>
                        <div
                          className="condo-hotel-card__trust-you-icon"
                          style={{ opacity: hideRating ? 0 : 1 }}
                        >
                          <StarSvg />
                        </div>
                        <p
                          className="condo-hotel-card__trust-you-text"
                          style={{ opacity: hideRating ? 0 : 1 }}
                        >
                          <span className="condo-hotel-card__bold-text">
                            {renderNumber(rating.value, ONE)}
                          </span>
                          {rating.reviewsCount ? (
                            <>
                              ({renderNumber(rating.reviewsCount)} <FormattedMessage id="reviews" />
                              )
                            </>
                          ) : rating.value >= MINIMUM_RATING_TO_SHOW ? (
                            <FormattedMessage
                              id={getCondoTextRating(Number(renderNumber(rating.value, ONE)))}
                            />
                          ) : null}
                        </p>
                      </>
                    ) : null}
                  </div>
                  <div className="condo-hotel-card__supplier">{supplierIdentifier}</div>
                </div>
              </div>
            </div>
            <div className="condo-hotel-card__price-content">
              {isExactDisabled && !isAnytime ? (
                <div className="condo-hotel-card__exact-disabled">
                  <RedWarningSvg /> <FormattedMessage id="rental.not.available" />
                </div>
              ) : (
                <>
                  <div className="condo-hotel-card__content-left">
                    {publicPrice && (
                      <div className="condo-hotel-card__price-public">
                        <p className="condo-hotel-card__public-text">
                          <FormattedMessage id="avg.public.price" />
                        </p>
                        <p className="condo-hotel-card__public-text">
                          ${renderNumber(Math.floor(publicPrice))}
                        </p>
                      </div>
                    )}
                    {!!savings && isSavingsExisted && (
                      <div className="condo-hotel-card__price-savings">
                        <p className="condo-hotel-card__savings-text">
                          <FormattedMessage id="savings" />
                        </p>
                        <p className="condo-hotel-card__savings-text">${renderNumber(savings)}</p>
                      </div>
                    )}
                  </div>
                  <div className="condo-hotel-card__content-right">
                    {price && (
                      <div className="condo-hotel-card__price-total">
                        <p className="condo-hotel-card__price-total-text">
                          ${renderNumber(Math.floor(price))}
                        </p>
                        <div className="condo-hotel-card__for-info">
                          <p className="condo-hotel-card__for-info-text">
                            <FormattedMessage id="for" />{' '}
                            <FormattedMessage id="nights" values={{ count: totalNights }} />
                          </p>
                          <Tooltip title={<FormattedMessage id="price.includes.taxes" />}>
                            <span className="condo-hotel-card__price-info-icon">
                              <InfoSvg />
                            </span>
                          </Tooltip>
                        </div>
                      </div>
                    )}
                  </div>
                  <div
                    className="condo-hotel-card__price-content-bottom"
                    onClick={this.onCondoPage}
                  >
                    <a className="condo-hotel-card__price-btn">
                      <FormattedMessage id="view.deal" />
                    </a>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    menuStore: state.navigationMenuStore,
  };
};

export const CondoHotelCard = connect(
  mapStateToProps,
  null,
)(withRouter(injectIntl(CondoHotelCardWrapper)));
